import { type EditorState, Plugin, type Transaction } from 'prosemirror-state'
import { type ResolvedPos } from 'prosemirror-model'

import { canJoin } from 'prosemirror-transform'
import { isEqual } from 'lodash'

import { BlockIdFixMetaKey } from 'proseShared/plugins/BlockId/BlockIdPlugin'
import { closeHistory } from 'prosemirror-history'
import { getListFormatAttrs } from 'common/Prose/utils'
import { mappedStepPositions } from './utils/mappedStepPositions'

// NOTE: This plugin watches for any change that puts two lists adjacent to
// each other and auto-joins them.
export const AutoJoinLists = new Plugin({
  appendTransaction: (trs: Transaction[], oldState: EditorState, newState: EditorState) => {
    const newTr = newState.tr
    if (trs.some((tr) => tr.getMeta('realTime'))) {
      // Disregard any transactions that came from real time
      return
    }

    mappedStepPositions(trs).forEach(({ from, to, tr }) => {
      // Skips meta transactions
      if (tr.getMeta(BlockIdFixMetaKey)) {
        return
      }

      // Approving tracked changes doesn't return good mappings
      if (joinResolvedPosition(newTr, newTr.doc.resolve(to), newState)) {
        return
      }

      const $from = newTr.doc.resolve(newTr.mapping.map(from))

      // if a list appears after another
      if (joinResolvedPosition(newTr, $from, newState)) {
        return
      }

      // if a list appears before another
      if (!$from.nodeAfter) {
        return
      }

      const $after = newTr.doc.resolve(newTr.mapping.map($from.pos + $from.nodeAfter.nodeSize))
      if (joinResolvedPosition(newTr, $after, newState)) {
        return
      }
    })

    if (newTr.docChanged) {
      return closeHistory(newTr)
    }
  },
})

function joinResolvedPosition(newTr: Transaction, resPos: ResolvedPos, newState: EditorState) {
  if (
    resPos.nodeAfter &&
    resPos.nodeBefore?.type === resPos.nodeAfter?.type &&
    resPos.nodeBefore?.type.groups.includes('list') &&
    resPos.nodeBefore?.type !== newState.schema.nodes.toggle_list &&
    isEqual(getListFormatAttrs(resPos.nodeBefore), getListFormatAttrs(resPos.nodeAfter)) &&
    canJoin(newTr.doc, resPos.pos)
  ) {
    newTr.join(resPos.pos)
    return true
  }
  return false
}
